import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '@omokage/shared';

// Re-export User type for convenience
export type { User };

/**
 * Authentication state interface
 */
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  isHydrated: boolean;
  lastFetchedAt: number | null; // キャッシュのタイムスタンプ
}

/**
 * Authentication actions interface
 */
interface AuthActions {
  setUser: (user: User) => void;
  updateUser: (updates: Partial<User>) => void;
  setLoading: (isLoading: boolean) => void;
  setInitialized: (isInitialized: boolean) => void;
  setHydrated: (isHydrated: boolean) => void;
  logout: () => void;
}

/**
 * Combined auth store type
 */
export type AuthStore = AuthState & AuthActions;

/**
 * Zustand auth store with persistence
 * Persists only user info and auth status to localStorage
 * Auth tokens are managed via HttpOnly cookies by the backend
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      isLoading: true,
      isInitialized: false,
      isHydrated: false,
      lastFetchedAt: null,

      // Actions
      setUser: (user) => {
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          lastFetchedAt: Date.now(),
        });
      },

      updateUser: (updates) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } });
        }
      },

      setLoading: (isLoading) => set({ isLoading }),

      setInitialized: (isInitialized) => set({ isInitialized }),

      setHydrated: (isHydrated) => set({ isHydrated }),

      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          lastFetchedAt: null,
        }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        lastFetchedAt: state.lastFetchedAt,
        // Note: isInitialized should NOT persist - it's a runtime flag
        // that must reset on each page load to trigger auth validation
      }),
      onRehydrateStorage: () => (state) => {
        // Called after rehydration completes
        state?.setHydrated(true);
      },
    }
  )
);

/**
 * Selector for user
 */
export const selectUser = (state: AuthStore) => state.user;

/**
 * Selector for auth status
 */
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated;

/**
 * Selector for loading status
 */
export const selectIsLoading = (state: AuthStore) => state.isLoading;
